import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** One piece of a changed line; `changed` marks the words that differ from the paired line. */
export interface DiffViewSegment {
    text: string;
    changed: boolean;
}

export interface DiffViewRow {
    type: 'add' | 'del' | 'same';
    oldNo?: number;
    newNo?: number;
    segments: DiffViewSegment[];
}

/** A run of unchanged lines folded away; `rows` are shown when the reviewer expands it. */
export interface DiffViewFold {
    type: 'fold';
    rows: DiffViewRow[];
}

export type DiffViewLine = DiffViewRow | DiffViewFold;

export interface DiffViewProps {
    /** Rows and folds in order (structurally the change requests' `LineDiff.lines`). */
    lines: DiffViewLine[];
    /** `+added −removed` above the table; omit to hide. */
    stats?: { added: number; removed: number };
    /** `unified` (default): one column, old/new line numbers; `side-by-side`: before | after. */
    kind?: 'unified' | 'side-by-side';
    /** For the accessible name: "<label> changes" ("Body", "Rules"). */
    label: string;
    /** Shown instead of the table when nothing changed. Default "No changes to the <label>." */
    empty?: ReactNode;
    className?: string;
}

function Segments(props: { row: DiffViewRow }) {
    const { segments } = props.row;
    return (
        <>
            {segments.map((segment, index) => (segment.changed ? <mark key={index}>{segment.text}</mark> : <span key={index}>{segment.text}</span>))}
            {segments.every(segment => !segment.text) ? ' ' : null}
        </>
    );
}

function UnifiedRow(props: { row: DiffViewRow }) {
    const { row } = props;
    const sign = row.type === 'add' ? '+' : row.type === 'del' ? '−' : ' ';
    return (
        <tr className={`ark-diff-row is-${row.type}`}>
            <td className="ark-diff-no" aria-hidden>{row.oldNo ?? ''}</td>
            <td className="ark-diff-no" aria-hidden>{row.newNo ?? ''}</td>
            <td className="ark-diff-sign" aria-hidden>{sign}</td>
            <td className="ark-diff-text"><Segments row={row} /></td>
        </tr>
    );
}

/** Pairs deleted and added rows of one change block side by side. */
function pairRows(rows: DiffViewRow[]): [DiffViewRow | undefined, DiffViewRow | undefined][] {
    const pairs: [DiffViewRow | undefined, DiffViewRow | undefined][] = [];
    let index = 0;
    while (index < rows.length) {
        const row = rows[index];
        if (row.type === 'same') {
            pairs.push([row, row]);
            index++;
            continue;
        }
        const dels: DiffViewRow[] = [];
        const adds: DiffViewRow[] = [];
        while (index < rows.length && rows[index].type === 'del') dels.push(rows[index++]);
        while (index < rows.length && rows[index].type === 'add') adds.push(rows[index++]);
        for (let k = 0; k < Math.max(dels.length, adds.length); k++) pairs.push([dels[k], adds[k]]);
    }
    return pairs;
}

function SideRow(props: { left?: DiffViewRow; right?: DiffViewRow }) {
    const { left, right } = props;
    const leftType = left ? (left.type === 'same' ? 'same' : 'del') : 'blank';
    const rightType = right ? (right.type === 'same' ? 'same' : 'add') : 'blank';
    return (
        <tr className="ark-diff-row">
            <td className="ark-diff-no" aria-hidden>{left?.oldNo ?? ''}</td>
            <td className={`ark-diff-text is-${leftType}`}>{left ? <Segments row={left} /> : null}</td>
            <td className="ark-diff-no" aria-hidden>{right?.newNo ?? ''}</td>
            <td className={`ark-diff-text is-${rightType}`}>{right ? <Segments row={right} /> : null}</td>
        </tr>
    );
}

/**
 * DS gap: a line diff on tokens (added / removed rows, word marks, folded unchanged runs that expand on click, `+n −n`
 * stats). Change requests own the model (`lineDiff`); this renders it (`ds.css` "diff view").
 */
export function DiffView(props: DiffViewProps) {
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const kind = props.kind ?? 'unified';
    const columns = 4;

    const unchanged = useMemo(() => props.lines.every(line => line.type === 'fold' || line.type === 'same'), [props.lines]);
    if (unchanged && (!props.stats || (props.stats.added === 0 && props.stats.removed === 0))) {
        return <div className="ark-diff-empty">{props.empty ?? `No changes to the ${props.label.toLowerCase()}.`}</div>;
    }

    const renderRows = (rows: DiffViewRow[], key: string) => kind === 'unified'
        ? rows.map((row, k) => <UnifiedRow key={`${key}-${k}`} row={row} />)
        : pairRows(rows).map(([left, right], k) => <SideRow key={`${key}-${k}`} left={left} right={right} />);

    // consecutive rows are grouped so side-by-side can pair deletions with additions
    const body: ReactNode[] = [];
    let run: DiffViewRow[] = [];
    let runStart = 0;
    const flush = () => {
        if (run.length) body.push(...renderRows(run, `r${runStart}`));
        run = [];
    };
    props.lines.forEach((line, index) => {
        if (line.type !== 'fold') {
            if (run.length === 0) runStart = index;
            run.push(line);
            return;
        }
        flush();
        if (expanded.has(index)) {
            body.push(...renderRows(line.rows, `f${index}`));
            return;
        }
        body.push(
            <tr key={`fold-${index}`} className="ark-diff-fold">
                <td colSpan={columns}>
                    <button type="button" onClick={() => setExpanded(prev => new Set(prev).add(index))}>
                        ⋯ Show {line.rows.length} unchanged line{line.rows.length === 1 ? '' : 's'}
                    </button>
                </td>
            </tr>,
        );
    });
    flush();

    return (
        <div className={['ark-diff', `is-${kind}`, props.className].filter(Boolean).join(' ')} aria-label={`${props.label} changes`} role="group">
            {props.stats ? (
                <div className="ark-diff-stats">
                    <span className="ark-diff-stat-add">+{props.stats.added}</span>
                    <span className="ark-diff-stat-del">−{props.stats.removed}</span>
                </div>
            ) : null}
            <div className="ark-diff-scroll">
                <table className="ark-diff-table">
                    <tbody>{body}</tbody>
                </table>
            </div>
        </div>
    );
}
